import React, { useState, useEffect, useRef, useContext, useCallback } from "react";
import { Link, useParams, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { parse } from "query-string";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Link as MuiLink,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import LockIcon from "@mui/icons-material/Lock";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { SettingsContext } from "context/SettingsContext";
import SessionClient from "util/SessionClient";
import { selectors, actions } from "rdx";
import messages from "rdx/modules/registration/messages";
import userMessages from "rdx/modules/users/messages";
import enrollmentMessages from "rdx/modules/enrollment/messages";
import types from "rdx/modules/registration/types";
import useLoading from "hooks/useLoading";
import { addressLookup } from "lib/helpers";
import { useUpgradeFromEntApplicationMutation } from "rdx/modules/enrollment/endpoints";
import LoginSocialGoogle from "containers/login/components/LoginForm/LoginSocialGoogle";
import { GoogleIcon } from "@icons";
import StaticPageLayout from "../../components/StaticPageLayout";
import * as styles from "./styles";

type TranslateFnT = (key: string, options?: Record<string, unknown>) => string;

type FormDataT = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  streetAddress: string;
  city: string;
  state: string;
  zip: string;
  cardholderName: string;
  password: string;
  confirmPassword: string;
};

type AddressSuggestionT = {
  street_line: string;
  city: string;
  state: string;
  zipcode: string;
};

type ApplicationAgreementT = {
  documentPath: string;
  message?: string;
  version?: string;
} | null;

type StepT = 1 | 2 | 3;

type FieldErrorsT = Partial<Record<keyof FormDataT, string>>;

const SIGNUP_SPONSOR_KEY = "signupSponsor";

const saveSignupSponsor = (sponsor?: string): boolean => {
  try {
    if (sponsor) {
      sessionStorage.setItem(SIGNUP_SPONSOR_KEY, sponsor);
    } else {
      sessionStorage.removeItem(SIGNUP_SPONSOR_KEY);
    }
    return true;
  } catch {
    return false;
  }
};

const consumeSignupSponsor = (): string | null => {
  try {
    const sponsor = sessionStorage.getItem(SIGNUP_SPONSOR_KEY);
    sessionStorage.removeItem(SIGNUP_SPONSOR_KEY);
    return sponsor;
  } catch {
    return null;
  }
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^\d{10}$/;
const ZIP_REGEX = /^\d{5}(-\d{4})?$/;

const PASSWORD_RULE_REGEXES = [
  { id: 1, regex: /^.{8,}$/, labelKey: "form.passwordRules.minLength" },
  { id: 2, regex: /(?=.*?[A-Z])/, labelKey: "form.passwordRules.uppercase" },
  { id: 3, regex: /(?=.*?[0-9])/, labelKey: "form.passwordRules.number" },
];

const formatPhoneInput = (value: string): string => {
  let digits = value.replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) {
    digits = digits.slice(1);
  }
  digits = digits.slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const stripPhone = (formatted: string): string => formatted.replace(/\D/g, "");

type StepIndicatorPropsT = {
  labels: string[];
  current: number;
};

const StepIndicator = ({ labels, current }: StepIndicatorPropsT) => (
  <Box sx={styles.stepIndicatorWrapper}>
    {labels.map((label, idx) => {
      const stepNum = idx + 1;
      const isComplete = stepNum < current;
      const isActive = stepNum === current;
      return (
        <React.Fragment key={label}>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.75 }}>
            <Box sx={styles.stepNumberCircle(isComplete, isActive)}>
              {isComplete ? (
                <CheckIcon sx={{ color: "#040922", fontSize: 18, fontWeight: 700 }} />
              ) : (
                <Typography sx={styles.stepNumberText(isActive)}>{stepNum}</Typography>
              )}
            </Box>
            <Typography sx={styles.stepLabel(isComplete, isActive)}>{label}</Typography>
          </Box>
          {idx < labels.length - 1 && <Box sx={styles.stepConnector(idx + 1 < current)} />}
        </React.Fragment>
      );
    })}
  </Box>
);

type Step1PropsT = {
  formData: FormDataT;
  onChange: (field: keyof FormDataT, value: string) => void;
  onNext: () => void;
  errors: FieldErrorsT;
  loading: boolean;
  eyebrowText: string;
  emailDisabled: boolean;
  personalFieldsReadOnly: boolean;
  googleVisible: boolean;
  googleConnected: boolean;
  googleConnectedName: string;
  googleConnecting: boolean;
  authUri: string | undefined;
  sponsor?: string;
  t: TranslateFnT;
};

const Step1 = ({
  formData,
  onChange,
  onNext,
  errors,
  loading,
  eyebrowText,
  emailDisabled,
  personalFieldsReadOnly,
  googleVisible,
  googleConnected,
  googleConnectedName,
  googleConnecting,
  authUri,
  sponsor,
  t,
}: Step1PropsT) => {
  const [addressSuggestions, setAddressSuggestions] = useState<AddressSuggestionT[]>([]);
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const requestIdRef = useRef(0);
  const phoneInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (googleConnected && phoneInputRef.current) {
      phoneInputRef.current.focus();
    }
  }, [googleConnected]);

  const handleBeforeGoogleRedirect = useCallback(() => saveSignupSponsor(sponsor), [sponsor]);

  const handleAddressInput = async (_: React.SyntheticEvent, value: string) => {
    onChange("streetAddress", value);
    const currentId = ++requestIdRef.current;
    const results = await addressLookup(value);
    if (currentId !== requestIdRef.current) return;
    setAddressSuggestions(results ?? []);
  };

  const handleAddressSelect = (_: React.SyntheticEvent, value: string | AddressSuggestionT | null) => {
    if (value && typeof value === "object") {
      onChange("streetAddress", value.street_line);
      onChange("city", value.city);
      onChange("state", value.state);
      onChange("zip", value.zipcode);
      setAddressSuggestions([]);
    }
  };

  return (
    <Box>
      <Typography sx={styles.eyebrow}>{eyebrowText}</Typography>
      <Typography variant="h3" sx={styles.stepHeading}>
        {t("form.step1.headingBase")}
        <Box component="span" sx={styles.gradientText}>
          {t("form.step1.headingGradient")}
        </Box>
      </Typography>
      <Typography sx={styles.stepSubtext}>{t("form.step1.subtext")}</Typography>

      {googleVisible && (
        <>
          <LoginSocialGoogle authUri={authUri} redirectPath="/signup" onBeforeRedirect={handleBeforeGoogleRedirect}>
            <Button variant="outlined" sx={styles.googleButton} startIcon={<GoogleIcon />}>
              {t("cta.signUpWithGoogle")}
            </Button>
          </LoginSocialGoogle>
          <Box sx={styles.orDivider}>{t("form.step1.orDivider")}</Box>
        </>
      )}

      {googleConnecting && (
        <Box sx={styles.googleConnectedBanner} role="status" aria-live="polite">
          <Box sx={styles.googleConnectedIconWrap}>
            <CircularProgress size={18} sx={{ color: "#20C2F1" }} />
          </Box>
          <Box>
            <Typography sx={styles.googleConnectedTitle}>{t("form.step1.googleConnectingTitle")}</Typography>
            <Typography sx={styles.googleConnectedSubtitle}>{t("form.step1.googleConnectingSubtitle")}</Typography>
          </Box>
        </Box>
      )}

      {googleConnected && !googleConnecting && (
        <Box sx={styles.googleConnectedBanner}>
          <Box sx={styles.googleConnectedIconWrap}>
            <GoogleIcon />
          </Box>
          <Box>
            <Typography sx={styles.googleConnectedTitle}>
              {t("form.step1.googleConnectedTitle", { name: googleConnectedName })}
            </Typography>
            <Typography sx={styles.googleConnectedSubtitle}>{t("form.step1.googleConnectedSubtitle")}</Typography>
          </Box>
        </Box>
      )}

      <Grid container spacing={2.5}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={t("form.labels.firstName")}
            value={formData.firstName}
            onChange={(e) => onChange("firstName", e.target.value)}
            sx={styles.darkInput}
            required
            error={!!errors.firstName}
            helperText={errors.firstName}
            disabled={personalFieldsReadOnly}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={t("form.labels.lastName")}
            value={formData.lastName}
            onChange={(e) => onChange("lastName", e.target.value)}
            sx={styles.darkInput}
            required
            error={!!errors.lastName}
            helperText={errors.lastName}
            disabled={personalFieldsReadOnly}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={t("form.labels.email")}
            type="email"
            value={formData.email}
            onChange={(e) => onChange("email", e.target.value)}
            sx={styles.darkInput}
            required
            error={!!errors.email}
            helperText={errors.email}
            disabled={emailDisabled || personalFieldsReadOnly || googleConnected}
            InputProps={
              googleConnected
                ? {
                    endAdornment: (
                      <InputAdornment position="end">
                        <CheckCircleIcon sx={{ color: "#20C2F1", fontSize: 18 }} aria-label="Verified by Google" />
                      </InputAdornment>
                    ),
                  }
                : undefined
            }
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={t("form.labels.phone")}
            value={formData.phone}
            onChange={(e) => onChange("phone", formatPhoneInput(e.target.value))}
            sx={styles.darkInput}
            required
            error={!!errors.phone}
            helperText={errors.phone}
            placeholder={t("form.placeholders.phone")}
            disabled={personalFieldsReadOnly}
            inputRef={phoneInputRef}
          />
        </Grid>
        <Grid item xs={12}>
          <Autocomplete
            freeSolo
            options={addressSuggestions}
            getOptionLabel={(option) => (typeof option === "string" ? option : option.street_line)}
            inputValue={formData.streetAddress}
            onInputChange={handleAddressInput}
            onChange={handleAddressSelect}
            filterOptions={(options, state) => {
              const query = state.inputValue.trim().toLowerCase();
              if (!query) return options;
              return options.filter(
                (option) => typeof option !== "string" && option.street_line.toLowerCase().startsWith(query),
              );
            }}
            renderOption={(optionProps, option) => {
              const addressKey = `${option.street_line}-${option.city}-${option.state}-${option.zipcode}`;
              return (
                <li {...optionProps} key={addressKey}>
                  {`${option.street_line} ${option.city}, ${option.state} ${option.zipcode}`}
                </li>
              );
            }}
            componentsProps={{
              paper: {
                sx: {
                  background: "rgba(7,20,55,0.95)",
                  backdropFilter: "blur(16px)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  borderRadius: "10px",
                  mt: 0.5,
                  boxShadow: "0 12px 40px rgba(0,0,0,0.5)",
                  "& .MuiAutocomplete-listbox": {
                    py: 0.5,
                    "& .MuiAutocomplete-option": {
                      fontFamily: "'Barlow', sans-serif",
                      fontSize: "0.9rem",
                      color: "rgba(255,255,255,0.85)",
                      px: 2,
                      py: 1,
                      borderBottom: "1px solid rgba(255,255,255,0.05)",
                      "&:last-child": { borderBottom: "none" },
                      "&[aria-selected='true']": {
                        background: "rgba(56,189,248,0.15)",
                      },
                      "&:hover": {
                        background: "rgba(56,189,248,0.1)",
                      },
                    },
                    "&:not(:hover) .MuiAutocomplete-option.Mui-focused": {
                      background: "rgba(56,189,248,0.1)",
                    },
                  },
                  "& .MuiAutocomplete-noOptions": {
                    fontFamily: "'Barlow', sans-serif",
                    fontSize: "0.85rem",
                    color: "rgba(255,255,255,0.5)",
                  },
                },
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                label={t("form.labels.streetAddress")}
                sx={styles.darkInput}
                required
                error={!!errors.streetAddress}
                helperText={errors.streetAddress}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={5}>
          <TextField
            fullWidth
            label={t("form.labels.city")}
            value={formData.city}
            onChange={(e) => onChange("city", e.target.value)}
            sx={styles.darkInput}
            required
            error={!!errors.city}
            helperText={errors.city}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            label={t("form.labels.state")}
            value={formData.state}
            onChange={(e) => onChange("state", e.target.value)}
            sx={styles.darkInput}
            required
            error={!!errors.state}
            helperText={errors.state}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField
            fullWidth
            label={t("form.labels.zip")}
            value={formData.zip}
            onChange={(e) => onChange("zip", e.target.value)}
            sx={styles.darkInput}
            required
            error={!!errors.zip}
            helperText={errors.zip}
          />
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={ageConfirmed}
                onChange={(e) => setAgeConfirmed(e.target.checked)}
                sx={{
                  color: "rgba(255,255,255,0.3)",
                  "&.Mui-checked": { color: "#20C2F1" },
                }}
              />
            }
            label={
              <Typography
                sx={{ fontFamily: "'Barlow', sans-serif", color: "rgba(255,255,255,0.82)", fontSize: "0.92rem" }}
              >
                {t("form.checkboxes.ageConfirmation")}
              </Typography>
            }
          />
        </Grid>
        <Grid item xs={12}>
          <Button
            fullWidth
            variant="contained"
            onClick={onNext}
            sx={styles.ctaButton}
            disabled={loading || !ageConfirmed}
          >
            {loading ? <CircularProgress size={24} sx={{ color: "white" }} /> : t("cta.continue")}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

type Step2PropsT = {
  formData: FormDataT;
  onChange: (field: keyof FormDataT, value: string) => void;
  onNext: () => void;
  onBack: () => void;
  errors: FieldErrorsT;
  loading: boolean;
  stripeError: string | null;
  enrollmentDeclineError: string | null;
  isEnterprisePro: boolean;
  t: TranslateFnT;
};

const stripeElementStyle = {
  base: {
    fontSize: "16px",
    fontFamily: "'Barlow', sans-serif",
    color: "rgba(255,255,255,0.9)",
    letterSpacing: "0.025em",
    "::placeholder": {
      color: "rgba(255,255,255,0.35)",
    },
  },
  invalid: {
    color: "#f87171",
  },
};

const Step2 = ({
  formData,
  onChange,
  onNext,
  onBack,
  errors,
  loading,
  stripeError,
  enrollmentDeclineError,
  isEnterprisePro,
  t,
}: Step2PropsT) => {
  const activateLabel = isEnterprisePro ? t("cta.activateEnterprise") : t("cta.activateSeller");
  const planBulletKeys = [
    "plan.bullets.fullAccess",
    "plan.bullets.proposalTools",
    "plan.bullets.training",
    "plan.bullets.commissions",
  ];

  return (
    <Box>
      {enrollmentDeclineError && (
        <Alert severity="error" sx={{ mb: 3, fontFamily: "'Barlow', sans-serif", borderRadius: "12px" }}>
          {enrollmentDeclineError}
        </Alert>
      )}
      <Typography sx={styles.eyebrow}>{t("form.step2.eyebrow")}</Typography>
      <Typography variant="h3" sx={styles.step2Heading}>
        {t("form.step2.headingBase")}
        <Box component="span" sx={styles.gradientText}>
          {t("form.step2.headingGradient")}
        </Box>
      </Typography>

      <Box sx={styles.planCard}>
        <Box
          sx={{
            position: "absolute",
            top: 0,
            right: 0,
            width: "60%",
            height: "60%",
            background: "radial-gradient(ellipse at top right, rgba(56,189,248,0.13) 0%, transparent 70%)",
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            width: "50%",
            height: "50%",
            background: "radial-gradient(ellipse at bottom left, rgba(103,232,249,0.09) 0%, transparent 70%)",
            pointerEvents: "none",
          }}
        />
        <Typography sx={styles.planPrice}>
          {isEnterprisePro ? t("plan.priceEnterprise") : t("plan.priceSeller")}
          <span style={{ fontSize: "1.4rem", fontWeight: 600 }}>{t("plan.perMonth")}</span>
        </Typography>
        <Typography sx={styles.planName}>
          {isEnterprisePro ? t("plan.enterprisePlatformName") : t("plan.sellerPlatformName")}
        </Typography>
        <Box sx={styles.planBulletList}>
          {planBulletKeys.map((key) => (
            <Box key={key} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box sx={styles.bulletCheckCircle}>
                <CheckIcon sx={{ fontSize: 13, color: "#20C2F1" }} />
              </Box>
              <Typography sx={styles.bulletText}>{t(key)}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <Grid container spacing={2.5}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label={t("form.labels.cardholderName")}
            value={formData.cardholderName}
            onChange={(e) => onChange("cardholderName", e.target.value)}
            sx={styles.darkInput}
            required
            error={!!errors.cardholderName}
            helperText={errors.cardholderName}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography sx={styles.stripeFieldLabel}>{t("form.labels.cardNumber")}</Typography>
          <Box sx={styles.stripeElementWrapper}>
            <CardNumberElement options={{ style: stripeElementStyle, showIcon: true }} />
          </Box>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography sx={styles.stripeFieldLabel}>{t("form.labels.expirationDate")}</Typography>
          <Box sx={styles.stripeElementWrapper}>
            <CardExpiryElement options={{ style: stripeElementStyle }} />
          </Box>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography sx={styles.stripeFieldLabel}>{t("form.labels.cvc")}</Typography>
          <Box sx={styles.stripeElementWrapper}>
            <CardCvcElement options={{ style: stripeElementStyle }} />
          </Box>
        </Grid>
        {stripeError && (
          <Grid item xs={12}>
            <Typography sx={styles.stripeErrorText}>{stripeError}</Typography>
          </Grid>
        )}
        <Grid item xs={12}>
          <Typography sx={styles.paymentDisclaimer}>
            <LockIcon sx={{ fontSize: 14, verticalAlign: "middle", mr: 0.5 }} />
            {t("form.payment.disclaimer")}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Button fullWidth variant="contained" onClick={onNext} sx={styles.ctaButton} disabled={loading}>
            {loading ? <CircularProgress size={24} sx={{ color: "white" }} /> : activateLabel}
          </Button>
        </Grid>
        <Grid item xs={12} sx={{ textAlign: "center" }}>
          <Button onClick={onBack} sx={styles.backButton}>
            {t("cta.back")}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

type Step3PropsT = {
  formData: FormDataT;
  onChange: (field: keyof FormDataT, value: string) => void;
  onSubmit: () => void;
  onBack: () => void;
  errors: FieldErrorsT;
  loading: boolean;
  applicationAgreement: ApplicationAgreementT;
  eyebrowText: string;
  hidePassword: boolean;
  ctaLabel: string;
  t: TranslateFnT;
};

const Step3 = ({
  formData,
  onChange,
  onSubmit,
  onBack,
  errors,
  loading,
  applicationAgreement,
  eyebrowText,
  hidePassword,
  ctaLabel,
  t,
}: Step3PropsT) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const agreementDocumentPath = applicationAgreement?.documentPath;

  const ruleResults = PASSWORD_RULE_REGEXES.map((rule) => ({
    ...rule,
    passed: rule.regex.test(formData.password),
  }));

  return (
    <Box>
      <Typography sx={styles.eyebrow}>{eyebrowText}</Typography>
      <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
        <CheckCircleIcon sx={{ color: "#20C2F1", fontSize: 72 }} />
      </Box>
      <Typography variant="h3" sx={styles.step3Heading}>
        {t("form.step3.heading")}
      </Typography>
      <Typography sx={styles.step3Subtext}>{t("form.step3.subtext")}</Typography>

      <Grid container spacing={2.5}>
        {!hidePassword && (
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t("form.labels.password")}
              type={showPassword ? "text" : "password"}
              value={formData.password}
              onChange={(e) => onChange("password", e.target.value)}
              sx={styles.darkInput}
              required
              error={!!errors.password}
              helperText={errors.password}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      sx={{ color: "rgba(255,255,255,0.5)" }}
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        )}
        {!hidePassword && (
          <Grid item xs={12}>
            <Box sx={styles.passwordRuleList}>
              {ruleResults.map((rule) => (
                <Box key={rule.id} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {rule.passed ? (
                    <CheckIcon sx={{ fontSize: 16, color: "#20C2F1" }} />
                  ) : (
                    <CloseIcon sx={{ fontSize: 16, color: "rgba(255,255,255,0.3)" }} />
                  )}
                  <Typography
                    sx={{
                      fontFamily: "'Barlow', sans-serif",
                      fontSize: "0.82rem",
                      color: rule.passed ? "#20C2F1" : "rgba(255,255,255,0.4)",
                      transition: "color 0.2s ease",
                    }}
                  >
                    {t(rule.labelKey)}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Grid>
        )}
        {!hidePassword && (
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t("form.labels.confirmPassword")}
              type={showConfirm ? "text" : "password"}
              value={formData.confirmPassword}
              onChange={(e) => onChange("confirmPassword", e.target.value)}
              sx={styles.darkInput}
              required
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirm(!showConfirm)}
                      edge="end"
                      sx={{ color: "rgba(255,255,255,0.5)" }}
                    >
                      {showConfirm ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        )}
        {agreementDocumentPath && (
          <Grid item xs={12}>
            <MuiLink
              href={agreementDocumentPath}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                fontFamily: "'Barlow', sans-serif",
                fontSize: "0.92rem",
                color: "#20C2F1",
                textDecoration: "underline",
                textUnderlineOffset: "3px",
                "&:hover": { color: "#67E8F9" },
              }}
            >
              {t("form.links.viewPoliciesAgreement")}
            </MuiLink>
          </Grid>
        )}
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                sx={{
                  color: "rgba(255,255,255,0.3)",
                  "&.Mui-checked": { color: "#20C2F1" },
                }}
              />
            }
            label={
              <Typography
                sx={{ fontFamily: "'Barlow', sans-serif", color: "rgba(255,255,255,0.82)", fontSize: "0.92rem" }}
              >
                {t("form.checkboxes.termsAcceptance")}
              </Typography>
            }
          />
        </Grid>
        <Grid item xs={12}>
          <Button
            fullWidth
            variant="contained"
            onClick={onSubmit}
            sx={styles.ctaButton}
            disabled={loading || !termsAccepted}
          >
            {loading ? <CircularProgress size={24} sx={{ color: "white" }} /> : ctaLabel}
          </Button>
        </Grid>
        <Grid item xs={12} sx={{ textAlign: "center" }}>
          <Button onClick={onBack} sx={styles.backButton}>
            {t("cta.back")}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

type SuccessStatePropsT = {
  t: TranslateFnT;
};

const SuccessState = ({ t }: SuccessStatePropsT) => {
  const { user } = useParams<{ user?: string }>();
  const prefix = user ? `/${user}` : "";
  return (
    <Box sx={{ textAlign: "center" }}>
      <CheckCircleIcon sx={{ color: "#20C2F1", fontSize: 80, mb: 2 }} />
      <Typography variant="h4" sx={styles.successHeading}>
        {t("success.heading")}
      </Typography>
      <Typography sx={styles.successSubtext}>{t("success.subtext")}</Typography>
      <Button component={Link} to={`${prefix}/join`} variant="contained" sx={styles.ctaButton}>
        {t("cta.goToDashboard")}
      </Button>
    </Box>
  );
};

const initialFormData: FormDataT = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  streetAddress: "",
  city: "",
  state: "",
  zip: "",
  cardholderName: "",
  password: "",
  confirmPassword: "",
};

type SellerSignupFormPropsT = {
  t: TranslateFnT;
};

const SellerSignupForm = ({ t }: SellerSignupFormPropsT) => {
  const { user, slug } = useParams<{ user?: string; slug?: string }>();
  const prefix = user ? `/${user}` : "";
  const history = useHistory();
  const dispatch = useDispatch();
  const stripe = useStripe();
  const elements = useElements();

  const [step, setStep] = useState<StepT>(1);
  const [formData, setFormData] = useState<FormDataT>(initialFormData);
  const [fieldErrors, setFieldErrors] = useState<FieldErrorsT>({});
  const [complete] = useState(false);
  const [stripeError, setStripeError] = useState<string | null>(null);
  const [stripeToken, setStripeToken] = useState<string | null>(null);
  const [authFormat, setAuthFormat] = useState<"password" | "google">("password");
  const [pendingStep, setPendingStep] = useState<
    "registration" | "update" | "stripe" | "createUser" | "retry" | "googleRegister" | null
  >(null);
  const [regIsLoaded, setRegIsLoaded] = useState(!slug);
  const [enrollmentDeclineError, setEnrollmentDeclineError] = useState<string | null>(null);
  const [isRetryMode, setIsRetryMode] = useState(false);

  const latestMessage = useSelector(selectors.getLatestMessageEvt);
  const regInfo = useSelector(selectors.getRegistration);
  const authUri = useSelector(selectors.getAuthUri);
  const applicationAgreement = useSelector(selectors.selectSellerApplicationAgreement);
  const requirePaymentData = useSelector(selectors.selectRequirePaymentData);
  const hasPendingSeat = !!regInfo?.pendingSeat;
  const isEnterprisePro = regInfo?.kind === "enterprise_pro" || regInfo?.kind === "powurline";
  const skipPayment =
    !!regInfo?.bypassPayment ||
    (regInfo?.kind === "powurline" && !!regInfo?.dealerEntBaselinePpw && !!regInfo?.dealerParentEnterpriseId);
  const registrationLoading = useLoading({
    watchRequests: [types.REGISTER_NEW_USER, types.UPDATE_REGISTRATION],
  });

  const userCreationLoading = useLoading({
    watchRequests: [types.CREATE_NEW_USER],
  });

  const [upgradeFromEntApplication] = useUpgradeFromEntApplicationMutation();

  const slugRef = useRef<string | null>(null);
  const sponsorRef = useRef<string | undefined>(user);

  const ambassadorSession = new SessionClient();
  const isAmbassador = !!ambassadorSession?.user && !!ambassadorSession.isAmbassador;
  const ambassadorId = isAmbassador ? ambassadorSession?.user?.props?.id : null;

  useEffect(() => {
    if (slug) {
      dispatch(actions.getRegistration({ code: slug }));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (regIsLoaded) {
      const enterpriseId = regInfo?.pendingSeat?.enterpriseId;
      dispatch(actions.getSellerApplicationAgreement(enterpriseId ? { enterpriseId } : {}));
    }
  }, [regIsLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (regInfo?.slug) {
      slugRef.current = regInfo.slug;
    }
  }, [regInfo]);

  useEffect(() => {
    if (!isAmbassador) return;
    const userProps = ambassadorSession?.user?.props;
    if (!userProps) return;
    setFormData((prev) => ({
      ...prev,
      firstName: userProps.firstName || prev.firstName,
      lastName: userProps.lastName || prev.lastName,
      email: userProps.email || prev.email,
      phone: userProps.phone ? formatPhoneInput(String(userProps.phone).replace(/\D/g, "")) : prev.phone,
    }));
  }, [isAmbassador]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const { code } = parse(history.location.search);
    if (slug || typeof code !== "string" || code === "undefined") return;
    const storedSponsor = consumeSignupSponsor();
    sponsorRef.current = user || storedSponsor || undefined;
    history.replace({ pathname: history.location.pathname, search: "" });
    setAuthFormat("google");
    setPendingStep("googleRegister");
    dispatch(
      actions.registerNewUser({
        authFormat: "google",
        authCode: code,
        redirectPath: "/signup",
        kind: "consultant",
        locale: "en",
        sponsor: sponsorRef.current,
      }),
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!latestMessage?.message) return;

    if (latestMessage.message === messages.REGISTER_NEW_USER_SUCCESS && pendingStep === "googleRegister") {
      const registrationSlug = regInfo?.slug || latestMessage.payload?.body?.slug;
      if (registrationSlug) slugRef.current = registrationSlug;
      setPendingStep(null);
      setRegIsLoaded(true);
      setFormData((prev) => ({
        ...prev,
        firstName: regInfo?.firstName || latestMessage.payload?.body?.first_name || prev.firstName,
        lastName: regInfo?.lastName || latestMessage.payload?.body?.last_name || prev.lastName,
        email: regInfo?.email || latestMessage.payload?.body?.email || prev.email,
      }));
      return;
    }

    if (latestMessage.message === messages.REGISTER_NEW_USER_SUCCESS && pendingStep === "registration") {
      const registrationSlug = regInfo?.slug || latestMessage.payload?.body?.slug;
      if (registrationSlug) {
        slugRef.current = registrationSlug;
        setPendingStep("update");
        dispatch(
          actions.updateRegistration({
            slug: registrationSlug,
            firstName: formData.firstName,
            lastName: formData.lastName,
            phone: stripPhone(formData.phone),
            street: formData.streetAddress,
            city: formData.city,
            state: formData.state,
            zip: formData.zip,
          }),
        );
      } else {
        setPendingStep(null);
        dispatch(
          actions.setAlertMessageVisible({
            message: t("alerts.registrationSlugMissing"),
            severity: "error",
          }),
        );
      }
    }

    if (latestMessage.message === messages.REGISTRATION_UPDATE_SUCCESS && pendingStep === "update") {
      setPendingStep(null);
      setStep(hasPendingSeat || skipPayment ? 3 : 2);
    }

    if (latestMessage.message === messages.ERROR_UPDATING_REGISTRATION) {
      setPendingStep(null);
      const specificMessage = (latestMessage as { error?: { message?: string } })?.error?.message;
      if (specificMessage) {
        dispatch(
          actions.setAlertMessageVisible({
            message: specificMessage,
            severity: "error",
          }),
        );
      }
    }

    if (latestMessage.message === messages.GET_REGISTRATION_SUCCESS) {
      if (regInfo?.confirmed) {
        history.push("/login");
        return;
      }
      if (regInfo?.inviteAt || isEnterprisePro || hasPendingSeat) {
        setFormData((prev) => ({
          ...prev,
          firstName: regInfo.firstName || prev.firstName,
          lastName: regInfo.lastName || prev.lastName,
          email: regInfo.email || prev.email,
          phone: regInfo.phone ? formatPhoneInput(regInfo.phone.replace(/\D/g, "")) : prev.phone,
          streetAddress: regInfo.address?.street || prev.streetAddress,
          city: regInfo.address?.city || prev.city,
          state: regInfo.address?.state || prev.state,
          zip: regInfo.address?.zip || prev.zip,
        }));
      }
      setRegIsLoaded(true);
    }

    if (latestMessage.message === messages.ERROR_GETTING_REGISTRATION) {
      history.push("/login");
    }

    if (latestMessage.message === messages.CREATE_NEW_USER_SUCCESS && pendingStep === "createUser") {
      dispatch(actions.acceptedLatestTermsOfServiceRequest());
      const enterpriseOwner = regInfo?.kind === "enterprise_pro";
      const seatedSeller = regInfo?.roleTags?.includes("consultant") && !!regInfo?.pendingSeat;
      const orgStaffRoles = ["org_manager", "org_support"];
      const orgStaff = regInfo?.roleTags?.some((role: string) => orgStaffRoles.includes(role));
      let navTarget = "/getting-started";
      if (enterpriseOwner || seatedSeller) navTarget = "/dashboard";
      else if (orgStaff) navTarget = "/org-projects/leads";
      const session = new SessionClient();
      const sessionUser = session.user;
      if (!sessionUser) {
        setPendingStep(null);
        history.push("/verify-email-required");
      } else {
        sessionUser
          .reload("seller-signup")
          .then(() => {
            setPendingStep(null);
            const finalTarget = session.user?.props?.emailVerified ? navTarget : "/verify-email-required";
            dispatch(
              actions.setAlertMessageVisible({
                message: t("alerts.accountCreatedSuccess"),
                severity: "success",
              }),
            );
            history.push(finalTarget);
          })
          .catch(() => {
            setPendingStep(null);
            history.push("/verify-email-required");
          });
      }
    }

    if (latestMessage.message === messages.ERROR_CREATING_NEW_USER) {
      setPendingStep(null);
      const specificMessage = (latestMessage as { error?: { message?: string } })?.error?.message;
      dispatch(
        actions.setAlertMessageVisible({
          message: specificMessage || t("alerts.accountCreatedError"),
          severity: "error",
        }),
      );
    }

    if (latestMessage.message === messages.ERROR_REGISTERING_NEW_USER) {
      setPendingStep(null);
      setTimeout(() => {
        dispatch(
          actions.setAlertMessageVisible({
            message: t("alerts.redirecting"),
            severity: "info",
          }),
        );
      }, 5000);
      setTimeout(() => {
        history.push("/login");
      }, 7000);
    }

    if (latestMessage.message === userMessages.UPGRADE_AMBASSADOR_SUCCESS && pendingStep === "createUser") {
      const session = new SessionClient();
      const sessionUser = session.user;
      if (!sessionUser) {
        setPendingStep(null);
        history.push("/getting-started");
      } else {
        sessionUser
          .reload("seller-signup")
          .then(() => {
            if (!session.user?.props?.emailVerified) {
              setPendingStep(null);
              history.push("/verify-email-required");
              return;
            }
            setPendingStep(null);
            dispatch(
              actions.setAlertMessageVisible({
                message: t("alerts.upgradeSuccess"),
                severity: "success",
              }),
            );
            history.push("/getting-started");
          })
          .catch(() => {
            setPendingStep(null);
            history.push("/getting-started");
          });
      }
    }

    if (latestMessage.message === userMessages.ERROR_UPGRADING_AMBASSADOR) {
      setPendingStep(null);
      const specificMessage = (latestMessage as { error?: { message?: string } })?.error?.message;
      dispatch(
        actions.setAlertMessageVisible({
          message: specificMessage || t("alerts.upgradeError"),
          severity: "error",
        }),
      );
    }

    if (latestMessage.message === enrollmentMessages.ENROLLMENT_SUCCESS && pendingStep === "retry") {
      setPendingStep(null);
      setEnrollmentDeclineError(null);
      setIsRetryMode(false);
      dispatch(
        actions.setAlertMessageVisible({
          message: t("alerts.accountCreatedSuccess"),
          severity: "success",
        }),
      );
      history.push("/getting-started");
    }

    if (latestMessage.message === enrollmentMessages.ENROLLMENT_ERROR && pendingStep === "retry") {
      setPendingStep(null);
      setEnrollmentDeclineError(t("form.errors.enrollmentDeclined"));
    }
  }, [latestMessage]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);

  useEffect(() => {
    if (!pendingStep) return undefined;
    const timeoutId = setTimeout(() => {
      setPendingStep(null);
      dispatch(
        actions.setAlertMessageVisible({
          message: t("alerts.requestTimeout"),
          severity: "error",
        }),
      );
    }, 35000);
    return () => clearTimeout(timeoutId);
  }, [pendingStep]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = useCallback((field: keyof FormDataT, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const validateStep1 = (): boolean => {
    const errs: FieldErrorsT = {};
    if (!formData.firstName.trim()) errs.firstName = t("form.errors.firstNameRequired");
    if (!formData.lastName.trim()) errs.lastName = t("form.errors.lastNameRequired");
    if (!formData.email.trim()) {
      errs.email = t("form.errors.emailRequired");
    } else if (!EMAIL_REGEX.test(formData.email)) {
      errs.email = t("form.errors.emailInvalid");
    }
    const phoneDigits = stripPhone(formData.phone);
    if (!phoneDigits) {
      errs.phone = t("form.errors.phoneRequired");
    } else if (!PHONE_REGEX.test(phoneDigits)) {
      errs.phone = t("form.errors.phoneInvalid");
    }
    if (!formData.streetAddress.trim()) errs.streetAddress = t("form.errors.streetAddressRequired");
    if (!formData.city.trim()) errs.city = t("form.errors.cityRequired");
    if (!formData.state.trim()) errs.state = t("form.errors.stateRequired");
    if (!formData.zip.trim()) {
      errs.zip = t("form.errors.zipRequired");
    } else if (!ZIP_REGEX.test(formData.zip)) {
      errs.zip = t("form.errors.zipInvalid");
    }
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleStep1Next = () => {
    if (!validateStep1()) return;
    if (isAmbassador) {
      setStep(2);
      return;
    }
    const effectiveSlug = slug || slugRef.current;
    if (effectiveSlug) {
      setPendingStep("update");
      dispatch(
        actions.updateRegistration({
          slug: effectiveSlug,
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: stripPhone(formData.phone),
          street: formData.streetAddress,
          city: formData.city,
          state: formData.state,
          zip: formData.zip,
        }),
      );
      return;
    }
    setPendingStep("registration");
    dispatch(
      actions.registerNewUser({
        email: formData.email,
        kind: "consultant",
        locale: "en",
        sponsor: sponsorRef.current,
        authFormat: "password",
      }),
    );
  };

  const validateStep2 = (): boolean => {
    const errs: FieldErrorsT = {};
    if (!formData.cardholderName.trim()) errs.cardholderName = t("form.errors.cardholderNameRequired");
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleStep2Next = async () => {
    if (!validateStep2()) return;
    if (!stripe || !elements) return;

    setStripeError(null);
    setPendingStep("stripe");

    const cardElement = elements.getElement(CardNumberElement);
    if (!cardElement) {
      setPendingStep(null);
      return;
    }

    const { error, token } = await stripe.createToken(cardElement, {
      name: formData.cardholderName,
    });

    if (error) {
      setStripeError(error.message || t("form.errors.stripeInvalidCard"));
      setPendingStep(null);
      return;
    }

    if (token) {
      setStripeToken(token.id);
      setPendingStep(null);
      setStep(3);
    }
  };

  const handleEnrollmentRetry = async () => {
    if (!validateStep2()) return;
    if (!stripe || !elements) return;

    setStripeError(null);
    setEnrollmentDeclineError(null);
    setPendingStep("stripe");

    const cardElement = elements.getElement(CardNumberElement);
    if (!cardElement) {
      setPendingStep(null);
      return;
    }

    const { error, token } = await stripe.createToken(cardElement, {
      name: formData.cardholderName,
    });

    if (error) {
      setStripeError(error.message || t("form.errors.stripeInvalidCard"));
      setPendingStep(null);
      return;
    }

    if (!token) {
      setPendingStep(null);
      return;
    }

    setPendingStep("retry");

    if (requirePaymentData) {
      try {
        await upgradeFromEntApplication({ tokenId: token.id, tos: true }).unwrap();
        setEnrollmentDeclineError(null);
        setIsRetryMode(false);
        dispatch(
          actions.setAlertMessageVisible({
            message: t("alerts.accountCreatedSuccess"),
            severity: "success",
          }),
        );
        history.push("/getting-started");
      } catch (e) {
        const declineMsg =
          (e as { error?: { data?: { error?: { message?: string } } } })?.error?.data?.error?.message ||
          t("form.errors.enrollmentDeclined");
        setPendingStep(null);
        setEnrollmentDeclineError(declineMsg);
      }
      return;
    }

    dispatch(
      actions.subscribeEnrollment({
        service: "powur_energy",
        token: token.id,
        tos: true,
        sourceType: "credit_card",
      }),
    );
  };

  const validateStep3 = (): boolean => {
    if (isAmbassador || authFormat === "google") return true;
    const errs: FieldErrorsT = {};
    if (!formData.password) {
      errs.password = t("form.errors.passwordRequired");
    } else {
      const allRulesPass = PASSWORD_RULE_REGEXES.every((rule) => rule.regex.test(formData.password));
      if (!allRulesPass) {
        errs.password = t("form.errors.passwordRequirements");
      }
    }
    if (!formData.confirmPassword) {
      errs.confirmPassword = t("form.errors.confirmPasswordRequired");
    } else if (formData.password !== formData.confirmPassword) {
      errs.confirmPassword = t("form.errors.passwordsMismatch");
    }
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = () => {
    if (!validateStep3()) return;

    if (isAmbassador) {
      if (!ambassadorId) return;
      setPendingStep("createUser");
      dispatch(
        actions.upgradeAmbassador({
          userId: ambassadorId,
          street: formData.streetAddress,
          city: formData.city,
          state: formData.state,
          zip: formData.zip,
          ...(stripeToken ? { token: stripeToken, sourceType: "credit_card" } : {}),
        }),
      );
      return;
    }

    const currentSlug = slugRef.current;
    if (!currentSlug) return;

    setPendingStep("createUser");
    const isGoogle = authFormat === "google";
    const passwordPayload = isGoogle ? {} : { password: formData.password, passwordConfirm: formData.confirmPassword };
    const cardPayload = stripeToken ? { token: stripeToken, sourceType: "credit_card", tos: true } : {};
    dispatch(
      actions.createNewUser({
        slug: currentSlug,
        ...passwordPayload,
        ...cardPayload,
      }),
    );
  };

  const handleBack = () => {
    setFieldErrors({});
    setStripeError(null);
    setEnrollmentDeclineError(null);
    setIsRetryMode(false);
    if (step === 3) {
      setFormData((prev) => ({ ...prev, cardholderName: "" }));
      setStripeToken(null);
      if (hasPendingSeat || skipPayment) {
        setStep(1);
        return;
      }
    }
    if (step > 1) setStep((step - 1) as StepT);
  };

  const isLoading =
    pendingStep === "registration" ||
    pendingStep === "update" ||
    pendingStep === "googleRegister" ||
    registrationLoading;
  const isStripeLoading = pendingStep === "stripe" || pendingStep === "retry";
  const isUserLoading = pendingStep === "createUser" || userCreationLoading;

  const stepLabels = [t("stepLabels.register"), t("stepLabels.activate"), t("stepLabels.confirm")];

  return (
    <Box sx={styles.pageWrapper}>
      <Box sx={styles.backgroundGradient} />

      <Container maxWidth="sm" sx={{ position: "relative", zIndex: 1, py: { xs: 3, md: 4 } }}>
        {!complete && (
          <StepIndicator
            labels={hasPendingSeat || skipPayment ? [t("stepLabels.register"), t("stepLabels.confirm")] : stepLabels}
            current={(hasPendingSeat || skipPayment) && step === 3 ? 2 : step}
          />
        )}

        <Box sx={styles.formCard}>
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "55%",
              height: "55%",
              background: "radial-gradient(ellipse at top left, rgba(56,189,248,0.14) 0%, transparent 70%)",
              pointerEvents: "none",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              right: 0,
              width: "50%",
              height: "50%",
              background: "radial-gradient(ellipse at bottom right, rgba(32,194,241,0.10) 0%, transparent 70%)",
              pointerEvents: "none",
            }}
          />
          {complete ? (
            <Box key="success" sx={{ animation: "fadeUp 0.5s ease both" }}>
              <SuccessState t={t} />
            </Box>
          ) : (
            <Box key={step} sx={{ animation: "fadeUp 0.45s ease both" }}>
              {step === 1 && (
                <Step1
                  sponsor={user}
                  formData={formData}
                  onChange={handleChange}
                  onNext={handleStep1Next}
                  errors={fieldErrors}
                  loading={!!isLoading}
                  eyebrowText={
                    hasPendingSeat || skipPayment ? t("form.step1.eyebrowOneOfTwo") : t("form.step1.eyebrowOneOfThree")
                  }
                  emailDisabled={isEnterprisePro}
                  personalFieldsReadOnly={isAmbassador}
                  googleVisible={!isAmbassador && !slug && !regInfo?.email && authFormat !== "google"}
                  googleConnected={authFormat === "google" && !!regInfo?.email}
                  googleConnectedName={
                    `${regInfo?.firstName || formData.firstName} ${regInfo?.lastName || formData.lastName}`.trim() ||
                    regInfo?.email ||
                    formData.email ||
                    ""
                  }
                  googleConnecting={pendingStep === "googleRegister"}
                  authUri={authUri}
                  t={t}
                />
              )}
              {step === 2 && (
                <Step2
                  formData={formData}
                  onChange={handleChange}
                  onNext={isRetryMode ? handleEnrollmentRetry : handleStep2Next}
                  onBack={handleBack}
                  errors={fieldErrors}
                  loading={isStripeLoading}
                  stripeError={stripeError}
                  enrollmentDeclineError={enrollmentDeclineError}
                  isEnterprisePro={isEnterprisePro}
                  t={t}
                />
              )}
              {step === 3 && (
                <Step3
                  formData={formData}
                  onChange={handleChange}
                  onSubmit={handleSubmit}
                  onBack={handleBack}
                  errors={fieldErrors}
                  loading={!!isUserLoading}
                  applicationAgreement={applicationAgreement}
                  eyebrowText={
                    hasPendingSeat || skipPayment
                      ? t("form.step3.eyebrowTwoOfTwo")
                      : t("form.step3.eyebrowThreeOfThree")
                  }
                  hidePassword={isAmbassador || authFormat === "google"}
                  ctaLabel={isAmbassador ? t("cta.completeUpgrade") : t("cta.createAccount")}
                  t={t}
                />
              )}
            </Box>
          )}
        </Box>

        <Box sx={{ mt: 3, display: "flex", justifyContent: "center" }}>
          <Button component={Link} to={`${prefix}/join`} startIcon={<ArrowBackIcon />} sx={styles.backToJoinButton}>
            {t("cta.backToJoin")}
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

const SellerSignupContent = () => {
  const { t } = useTranslation("static_seller_signup");
  const settings = useContext(SettingsContext);
  const stripeKey = settings?.stripe_api_key;

  if (!stripeKey) {
    return (
      <Box sx={{ ...styles.pageWrapper, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress sx={{ color: "#20C2F1" }} />
      </Box>
    );
  }

  const stripePromise = loadStripe(stripeKey);

  return (
    <Elements stripe={stripePromise}>
      <SellerSignupForm t={t} />
    </Elements>
  );
};

const SellerSignupPage = () => {
  const { t } = useTranslation("static_seller_signup");
  return (
    <StaticPageLayout title={t("meta.title")}>
      <SellerSignupContent />
    </StaticPageLayout>
  );
};

export default SellerSignupPage;
